import { z } from "zod";
import {
  MissionCollectedSummarySchema,
  MissionDeliveryStateSchema,
} from "./mission-collected-evidence.js";
import {
  CreateMissionSchema,
  MissionAssignmentPlanSchema,
  MissionAssignmentStatusSchema,
  MissionDecisionInputSchema,
  MissionEvidenceInputSchema,
  MissionEvidenceKindSchema,
  MissionPlanSchema,
  MissionPolicySchema,
  MissionRevisionsSchema,
  MissionStatusSchema,
} from "./missions.js";
import { TextReadPageInput } from "./text-read.js";

const id = z.string().min(1).max(100);
export const MissionSourceSchema = z.discriminatedUnion("kind", [
  z
    .object({ kind: z.literal("thread"), id, messageId: id.optional(), label: z.string().max(200) })
    .strict(),
  z.object({ kind: z.enum(["ticket", "artifact"]), id, label: z.string().max(200) }).strict(),
  z
    .object({
      kind: z.enum(["pull_request", "external_ticket", "reference"]),
      url: z
        .url()
        .max(2000)
        .refine((value) => {
          const url = new URL(value);
          return url.protocol === "https:" && !url.username && !url.password;
        }),
      label: z.string().max(200),
    })
    .strict(),
]);
export const MissionSourcesSchema = z.array(MissionSourceSchema).max(50);
export const MissionSettingsSchema = z
  .object({
    leadBotId: id.nullable().default(null),
    deviceId: id.nullable().default(null),
    integrationIds: z.array(id).max(100).default([]),
    policy: MissionPolicySchema.default(() => MissionPolicySchema.parse({})),
  })
  .strict();
export type MissionSettings = z.infer<typeof MissionSettingsSchema>;
export const MissionCreateInput = CreateMissionSchema.extend({
  sources: MissionSourcesSchema.default([]),
  settings: MissionSettingsSchema.default(() => MissionSettingsSchema.parse({})),
});
export const MissionCaptureInput = MissionCreateInput.omit({
  operationKey: true,
  sources: true,
}).extend({ source: MissionSourceSchema });
export const MissionPrepareInput = z.object({ source: MissionSourceSchema }).strict();
export const MissionPreparedSchema = z.object({
  operationKey: id,
  existingMissionId: id.nullable(),
  location: z.object({ web: z.string(), mobile: z.string() }).nullable(),
  title: z.string(),
  objective: z.string(),
  projectId: id.nullable(),
  leadBotId: id.nullable(),
  sources: MissionSourcesSchema,
});
export type MissionSource = z.infer<typeof MissionSourceSchema>;
export type MissionPrepared = z.infer<typeof MissionPreparedSchema>;
export const MissionReadInput = z.object({ id }).strict();
/** Tool-only view; ordinary service/API readers retain MissionReadInput. */
export const MissionToolReadInput = MissionReadInput.extend({
  ...TextReadPageInput.shape,
  view: z.enum(["detail", "plan"]).default("detail"),
  expectedVersion: z.number().int().nonnegative().optional(),
}).refine(
  (input) =>
    input.view === "plan" ||
    (input.offset === 0 && input.guard === undefined && input.expectedVersion === undefined),
  { message: "Continuation parameters require view: plan." },
);
export const MissionEvidenceReadInput = MissionReadInput.extend({
  assignmentKey: id,
  view: z.enum(["index", "files", "checks", "decisions", "record"]).default("index"),
  evidenceId: id.optional(),
  decisionId: id.optional(),
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().min(1).max(10).default(5),
}).strict();
export const MissionChangesInput = MissionReadInput.extend({
  afterSequence: z.number().int().nonnegative().default(0),
}).strict();
export const MissionChangesSchema = z.object({
  cursor: z.number(),
  version: z.number(),
  activityKey: z.string(),
  more: z.boolean(),
  events: z.array(
    z.object({
      sequence: z.number(),
      type: z.string(),
      createdAt: z.string(),
    }),
  ),
});
export const MissionListInput = z
  .object({
    status: MissionStatusSchema.optional(),
    projectId: id.optional(),
    archived: z.boolean().optional(),
  })
  .strict()
  .default({});
const edit = { id, version: z.number().int().nonnegative(), operationKey: id };
export const MissionCommandSchema = z.discriminatedUnion("action", [
  z.object({ ...edit, action: z.literal("archive"), archived: z.boolean() }).strict(),
  z.object({ ...edit, action: z.literal("prepare_plan") }).strict(),
  z.object({ ...edit, action: z.literal("review_policy"), enabled: z.boolean() }).strict(),
  z
    .object({ ...edit, action: z.literal("budget"), budgetUsd: z.number().finite().positive() })
    .strict(),
  z
    .object({
      ...edit,
      action: z.literal("reassign_evidence"),
      from: id,
      to: id,
      criterionKey: id,
      kind: MissionEvidenceKindSchema,
      reason: z.string().trim().min(1).max(2000),
    })
    .strict(),
  z.object({ ...edit, action: z.literal("retry_delivery"), decisionId: id }).strict(),
  z
    .object({
      ...edit,
      action: z.literal("coding_followup"),
      key: id,
      prompt: z.string().trim().min(1).max(16000),
    })
    .strict(),
  z
    .object({
      ...edit,
      action: z.literal("direction"),
      assignmentKey: id.nullable().default(null),
      text: z.string().trim().min(1).max(16000),
    })
    .strict(),
  z.object({ ...edit, action: z.literal("plan"), plan: MissionPlanSchema }).strict(),
  z.object({ ...edit, action: z.literal("status"), status: MissionStatusSchema }).strict(),
  z
    .object({
      ...edit,
      action: z.literal("assignment"),
      key: id,
      status: MissionAssignmentStatusSchema,
    })
    .strict(),
  z
    .object({
      ...edit,
      action: z.literal("evidence"),
      planRevision: z.number().int().positive(),
      evidence: MissionEvidenceInputSchema.omit({ operationKey: true }),
    })
    .strict(),
  z.object({ ...edit, action: z.literal("revisions"), revisions: MissionRevisionsSchema }).strict(),
  z.object({ ...edit, action: z.literal("sources"), sources: MissionSourcesSchema }).strict(),
  z
    .object({
      ...edit,
      action: z.literal("decision"),
      decision: MissionDecisionInputSchema.omit({ operationKey: true }),
    })
    .strict(),
  z
    .object({
      ...edit,
      action: z.literal("resolve"),
      decisionId: id,
      status: z.enum(["approved", "rejected", "answered"]),
      response: z.string().max(4000),
    })
    .strict(),
]);
export type MissionCommand = z.infer<typeof MissionCommandSchema>;
export const MissionCheckpointSchema = z
  .object({
    view: z.enum(["overview", "work", "history"]).default("overview"),
    assignmentKey: id.nullable().default(null),
    evidenceId: id.nullable().default(null),
    path: z.string().max(500).nullable().default(null),
  })
  .strict();
export type MissionCheckpoint = z.infer<typeof MissionCheckpointSchema>;
export const MissionCheckpointInput = z
  .object({ id, checkpoint: MissionCheckpointSchema })
  .strict();
export const MissionHistoryInput = z
  .object({ id, beforeSequence: z.number().int().nonnegative().optional() })
  .strict();
export const MissionHistoryPageSchema = z.object({
  events: z.array(
    z.object({
      id,
      sequence: z.number(),
      type: z.string(),
      createdAt: z.string(),
      payload: z.unknown(),
    }),
  ),
  nextBefore: z.number().nullable(),
});
export const MissionSummarySchema = z.object({
  id,
  projectId: id.nullable(),
  title: z.string(),
  objective: z.string(),
  status: MissionStatusSchema,
  version: z.number(),
  planRevision: z.number(),
  sources: MissionSourcesSchema,
  settings: MissionSettingsSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
  archivedAt: z.string().nullable().default(null),
  checkpoint: MissionCheckpointSchema.nullable().default(null),
});
export const MissionDetailSchema = MissionSummarySchema.extend({
  planReadiness: z.array(z.string()).default([]),
  operations: z
    .object({
      checkedAt: z.string(),
      recordedUsd: z.number(),
      reviewUsd: z.number(),
      unpricedCalls: z.number().int(),
      budgetUsd: z.number().nullable(),
      maxParallel: z.number().int(),
      active: z.number().int(),
      runs: z.array(
        z.object({
          dispatchId: id,
          assignmentKey: id.nullable(),
          attempt: z.number().int(),
          state: z.string(),
          queuedAt: z.string().nullable(),
          nextPollAt: z.string().nullable(),
          providerErrors: z.number().int(),
          lastActivityAt: z.string(),
          leaseExpiresAt: z.string().nullable(),
          recovery: z.string().nullable(),
          input: z
            .object({ runId: id, botId: id, kind: z.enum(["message", "computer"]) })
            .nullable()
            .default(null),
        }),
      ),
    })
    .nullable()
    .default(null),
  dispatches: z
    .array(
      z.object({
        id,
        kind: z.enum(["plan", "assignment", "recovery"]),
        planRevision: z.number().int(),
        assignmentKey: id.nullable(),
        attempt: z.number().int(),
        status: z.enum(["pending", "running", "completed", "failed", "cancelled"]),
        runId: id.nullable(),
        cloudAgentId: id.nullable().default(null),
        cloudAgent: z
          .object({
            id,
            remoteId: id.nullable(),
            latestRunId: id.nullable(),
            status: z.string(),
            url: z.string(),
            branch: z.string().nullable(),
            prUrl: z.string().nullable(),
            errorCount: z.number(),
            cancelRequested: z.boolean(),
          })
          .nullable()
          .default(null),
        error: z.string().nullable(),
        reviewError: z.string().nullable().default(null),
        reviewBlocker: z.string().nullable().default(null),
        reviewLeaseExpiresAt: z.string().nullable().default(null),
        createdAt: z.string(),
        updatedAt: z.string(),
      }),
    )
    .default([]),
  plan: MissionPlanSchema.nullable(),
  assignments: z.array(
    z.object({
      id,
      key: id,
      planRevision: z.number(),
      status: MissionAssignmentStatusSchema,
      attempts: z.number(),
      definition: MissionAssignmentPlanSchema,
      updatedAt: z.string(),
    }),
  ),
  evidence: z.array(
    MissionEvidenceInputSchema.extend({
      id,
      planRevision: z.number(),
      sequence: z.number(),
      provenance: z.enum(["agent", "collector"]),
      stale: z.boolean(),
      data: MissionCollectedSummarySchema.nullable().default(null),
      createdAt: z.string(),
    }).strip(),
  ),
  decisions: z.array(
    z.object({
      id,
      planRevision: z.number(),
      title: z.string(),
      action: MissionDecisionInputSchema.shape.action,
      assignmentKey: id.nullable().default(null),
      target: MissionDecisionInputSchema.shape.target,
      runId: id.nullable().default(null),
      messageId: id.nullable().default(null),
      details: z.string(),
      sourceRevisions: MissionRevisionsSchema,
      status: z.enum(["pending", "approved", "rejected", "answered", "stale", "superseded"]),
      response: z.string().nullable(),
      createdAt: z.string(),
      resolvedAt: z.string().nullable(),
      executedAt: z.string().nullable().default(null),
      executionError: z.string().nullable().default(null),
      deliveryState: MissionDeliveryStateSchema.nullable().default(null),
    }),
  ),
  events: z.array(
    z.object({
      id,
      sequence: z.number(),
      type: z.string(),
      createdAt: z.string(),
      payload: z.unknown(),
    }),
  ),
});
export type MissionDetail = z.infer<typeof MissionDetailSchema>;
export type MissionSummary = z.infer<typeof MissionSummarySchema>;
export const MISSION_READ_TOOLS = [
  "mission_list",
  "mission_get",
  "mission_evidence",
  "mission_prepare",
  "mission_repository_file",
  "mission_inspect_repository",
  "mission_check_log",
] as const;
export const MISSION_WRITE_TOOLS = [
  "mission_retry_review",
  "mission_propose_guide",
  "mission_apply_guide",
  "mission_create",
  "mission_capture",
  "mission_update",
  "mission_collect_repository",
  "mission_collect_preview",
  "mission_collect_ci_captures",
  "mission_capture_visual",
] as const;
export const MISSION_TOOLS = [...MISSION_READ_TOOLS, ...MISSION_WRITE_TOOLS] as const;

export const MissionDraftSchema = z.object({
  text: z.string().max(16000),
  target: z.string().max(100),
  submission: z
    .object({
      fingerprint: z.string().max(128000),
      version: z.number().int().nonnegative(),
      operationKey: z.string().min(1).max(100),
    })
    .nullable()
    .default(null),
});
export type MissionDraft = z.infer<typeof MissionDraftSchema>;
